import { useEffect, useMemo, useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  CronBuilderMode,
  CronTimerValue,
  DEFAULT_SIMPLE_SCHEDULE,
  SimpleScheduleState,
  Weekday,
  WEEKDAY_OPTIONS,
  WEEKDAY_SEQUENCE,
  buildCronFromSimpleState,
  deriveModeFromValue,
  getTimezoneList,
  isValidCronExpression,
  parseCronToSimpleState,
  summarizeSimpleState,
} from "./cronUtils";
import { cn } from "@/lib/utils";

interface CronTimerFieldProps {
  value?: CronTimerValue;
  onChange: (value: CronTimerValue) => void;
  disabled?: boolean;
}

const defaultCronValue = buildCronFromSimpleState(DEFAULT_SIMPLE_SCHEDULE);

const serializeCronValue = (value?: CronTimerValue): string =>
  JSON.stringify({ cron: value?.cron ?? "", timezone: value?.timezone ?? "" });

const parseInputNumber = (value: string, fallback: number): number => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

export function CronTimerField({
  value,
  onChange,
  disabled = false,
}: CronTimerFieldProps) {
  const lastEmittedValueRef = useRef<string | null>(null);
  const initialSimpleState =
    parseCronToSimpleState(value?.cron ?? "") ?? DEFAULT_SIMPLE_SCHEDULE;
  const [simpleState, setSimpleState] =
    useState<SimpleScheduleState>(initialSimpleState);
  const [advancedCron, setAdvancedCron] = useState<string>(
    value?.cron ?? defaultCronValue,
  );
  const [timezone, setTimezone] = useState<string>(value?.timezone ?? "UTC");
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [intervalInput, setIntervalInput] = useState(
    String(initialSimpleState.interval),
  );
  const [minuteOffsetInput, setMinuteOffsetInput] = useState(
    String(initialSimpleState.minute),
  );

  const derivedMode = deriveModeFromValue(value);
  const [mode, setMode] = useState<CronBuilderMode>(derivedMode);

  useEffect(() => {
    const serialized = serializeCronValue(value);
    if (serialized === lastEmittedValueRef.current) {
      lastEmittedValueRef.current = null;
      return;
    }
    setMode(derivedMode);
  }, [derivedMode, value]);

  useEffect(() => {
    if (value?.cron) {
      setAdvancedCron(value.cron);
      const parsed = parseCronToSimpleState(value.cron);
      if (parsed) {
        setSimpleState(parsed);
      }
    }
    setTimezone(value?.timezone ?? "UTC");
  }, [value?.cron, value?.timezone]);

  useEffect(() => {
    setIntervalInput(String(simpleState.interval));
  }, [simpleState.interval]);

  useEffect(() => {
    setMinuteOffsetInput(String(simpleState.minute));
  }, [simpleState.minute]);

  const timezoneOptions = useMemo(() => getTimezoneList(), []);
  const ensureWeekdaySelection = (days: Weekday[]): Weekday[] =>
    days.length > 0 ? days : [...WEEKDAY_SEQUENCE];

  const emitValue = (next: CronTimerValue) => {
    lastEmittedValueRef.current = serializeCronValue(next);
    onChange(next);
  };

  const handleModeChange = (nextMode: CronBuilderMode) => {
    setMode(nextMode);

    if (nextMode === "simple") {
      const cronExpr = buildCronFromSimpleState(simpleState);
      setAdvancedCron(cronExpr);
      emitValue({ cron: cronExpr, timezone });
      return;
    }

    emitValue({ cron: advancedCron, timezone });
  };

  const handleSimpleStateChange = (updates: Partial<SimpleScheduleState>) => {
    setSimpleState((prev) => {
      const next = { ...prev, ...updates };

      if (
        (updates.cadence === "daily" || updates.cadence === "weekly") &&
        (!updates.daysOfWeek || updates.daysOfWeek.length === 0)
      ) {
        next.daysOfWeek = ensureWeekdaySelection(prev.daysOfWeek);
      }

      if (
        (next.cadence === "daily" || next.cadence === "weekly") &&
        next.daysOfWeek.length === 0
      ) {
        next.daysOfWeek = ensureWeekdaySelection(next.daysOfWeek);
      }

      if (mode === "simple") {
        const cronExpr = buildCronFromSimpleState(next);
        setAdvancedCron(cronExpr);
        emitValue({ cron: cronExpr, timezone });
      }
      return next;
    });
  };

  const handleIntervalInputChange = (raw: string) => {
    setIntervalInput(raw);
    if (raw === "") {
      return;
    }
    const numeric = parseInt(raw, 10);
    if (Number.isNaN(numeric)) {
      return;
    }
    handleSimpleStateChange({ interval: Math.max(1, numeric) });
  };

  const handleIntervalInputBlur = () => {
    if (intervalInput === "") {
      setIntervalInput("1");
      handleSimpleStateChange({ interval: 1 });
      return;
    }
    const numeric = Math.max(1, parseInt(intervalInput, 10) || 1);
    setIntervalInput(String(numeric));
    handleSimpleStateChange({ interval: numeric });
  };

  const handleMinuteOffsetInputChange = (raw: string) => {
    setMinuteOffsetInput(raw);
    if (raw === "") {
      return;
    }
    const numeric = parseInt(raw, 10);
    if (Number.isNaN(numeric)) {
      return;
    }
    handleSimpleStateChange({ minute: Math.min(59, Math.max(0, numeric)) });
  };

  const handleMinuteOffsetInputBlur = () => {
    const numeric = Math.min(
      59,
      Math.max(0, parseInt(minuteOffsetInput, 10) || 0),
    );
    setMinuteOffsetInput(String(numeric));
    handleSimpleStateChange({ minute: numeric });
  };

  const handleFrequencyChange = (nextValue: string) => {
    const cadence = nextValue as SimpleScheduleState["cadence"];
    if (cadence === "daily" || cadence === "weekly") {
      handleSimpleStateChange({
        cadence,
        daysOfWeek: ensureWeekdaySelection(simpleState.daysOfWeek),
      });
      return;
    }
    handleSimpleStateChange({ cadence });
  };

  const handleTimezoneChange = (nextTimezone: string) => {
    setTimezone(nextTimezone);
    setTimezoneOpen(false);
    emitValue({ cron: advancedCron, timezone: nextTimezone });
  };

  const handleAdvancedCronChange = (nextCron: string) => {
    setAdvancedCron(nextCron);
    if (mode === "advanced") {
      emitValue({ cron: nextCron, timezone });
    }
  };

  const renderWeekdayControls = () => (
    <div className="space-y-2">
      <Label>
        {simpleState.cadence === "daily" ? "Run on" : "Days of week"}
      </Label>
      <ToggleGroup
        type="multiple"
        className="flex-wrap"
        value={simpleState.daysOfWeek}
        onValueChange={(vals) => {
          if (disabled) {
            return;
          }
          handleSimpleStateChange({
            daysOfWeek: vals as SimpleScheduleState["daysOfWeek"],
          });
        }}
      >
        {WEEKDAY_OPTIONS.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            aria-label={option.label}
            disabled={disabled}
          >
            {option.label.slice(0, 3)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <p className="text-xs text-muted-foreground">
        Select one or more days to run this schedule.
      </p>
    </div>
  );

  const showWeekdaySelector =
    simpleState.cadence === "weekly" || simpleState.cadence === "daily";
  const showIntervalInput = !showWeekdaySelector;
  const showTimeInput = simpleState.cadence === "days" || showWeekdaySelector;
  const showMinuteOffset = simpleState.cadence === "hours";

  const timezoneControl = (
    <div className="space-y-2">
      <Label>Timezone</Label>
      <Popover
        open={!disabled && timezoneOpen}
        onOpenChange={(open) => {
          if (disabled) {
            return;
          }
          setTimezoneOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className="w-full justify-between"
            disabled={disabled}
          >
            {timezone || "Select timezone"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command>
            <CommandInput placeholder="Search timezone..." />
            <CommandList>
              <CommandEmpty>No timezone found.</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-y-auto">
                {timezoneOptions.map((tz) => (
                  <CommandItem
                    key={tz}
                    value={tz}
                    onSelect={(currentValue) =>
                      handleTimezoneChange(currentValue)
                    }
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        timezone === tz ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {tz}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <div className="space-y-4">
      <Tabs
        value={mode}
        onValueChange={(nextModeValue) => {
          if (disabled) {
            return;
          }
          handleModeChange(nextModeValue as CronBuilderMode);
        }}
      >
        <TabsList className="grid grid-cols-2">
          <TabsTrigger value="simple" disabled={disabled}>
            Simple
          </TabsTrigger>
          <TabsTrigger value="advanced" disabled={disabled}>
            Advanced
          </TabsTrigger>
        </TabsList>
        <TabsContent value="simple" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select
                value={simpleState.cadence}
                disabled={disabled}
                onValueChange={handleFrequencyChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Every N minutes</SelectItem>
                  <SelectItem value="hours">Every N hours</SelectItem>
                  <SelectItem value="days">Every N days</SelectItem>
                  <SelectItem value="daily">Daily (choose days)</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {showIntervalInput && (
              <div className="space-y-2">
                <Label>Interval</Label>
                <Input
                  type="number"
                  min={1}
                  value={intervalInput}
                  disabled={disabled}
                  inputMode="numeric"
                  onChange={(event) =>
                    handleIntervalInputChange(event.target.value)
                  }
                  onBlur={handleIntervalInputBlur}
                />
              </div>
            )}
          </div>

          {showMinuteOffset && (
            <div className="space-y-2">
              <Label>Minute offset</Label>
              <Input
                type="number"
                min={0}
                max={59}
                value={minuteOffsetInput}
                disabled={disabled}
                inputMode="numeric"
                onChange={(event) =>
                  handleMinuteOffsetInputChange(event.target.value)
                }
                onBlur={handleMinuteOffsetInputBlur}
              />
            </div>
          )}

          {showTimeInput && (
            <div className="space-y-2">
              <Label>Time of day</Label>
              <Input
                type="time"
                value={`${simpleState.hour.toString().padStart(2, "0")}:${simpleState.minute
                  .toString()
                  .padStart(2, "0")}`}
                disabled={disabled}
                onChange={(event) => {
                  const [hour, minute] = event.target.value
                    .split(":")
                    .map((part, index) =>
                      parseInputNumber(
                        part,
                        index === 0 ? simpleState.hour : simpleState.minute,
                      ),
                    );
                  handleSimpleStateChange({ hour, minute });
                }}
              />
            </div>
          )}

          {showWeekdaySelector && renderWeekdayControls()}

          <p className="text-sm text-muted-foreground">
            {summarizeSimpleState(simpleState, timezone)}
          </p>

          {timezoneControl}
        </TabsContent>

        <TabsContent value="advanced" className="space-y-4">
          <div className="space-y-2">
            <Label>cron expression</Label>
            <Input
              value={advancedCron}
              disabled={disabled}
              onChange={(event) => handleAdvancedCronChange(event.target.value)}
              placeholder="0 * * * *"
            />
            {!isValidCronExpression(advancedCron) && (
              <p className="text-xs text-destructive">
                Cron expressions must contain five space-separated fields.
              </p>
            )}
          </div>
          {timezoneControl}
          <p className="text-sm text-muted-foreground">
            Runs using the expression{" "}
            <code className="font-mono">{advancedCron}</code>
            {timezone ? ` in ${timezone}` : ""}.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
